import { useContext, useState, useEffect, useCallback, useMemo } from 'react'
import {
  AgxRow,
  AgxButton,
  AgxModal,
  Icon_Email,
} from '@urbanx/agx.ui-components2'
import { CustomEmailParagraph } from 'types/agencySettings'
import LexicalEditor from '../../components/lexical-editor/LexicalEditor'
import EmailPreview from './EmailPreview'
import GetSendGridTemplate from 'services/functions/notification/getSendGridTemplate'
import { AuthContext } from '@urbanx/agx.auth.client'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import './CustomParagraphModal.scss'
import { LicenceDetailsState } from '@urbanx/agx.agencies.client'

interface CustomParagraphModalProps {
  customEmailParagraph: CustomEmailParagraph | undefined
  title: string
  templateName: string
  initialState: LicenceDetailsState
  saveCustomParagraph: (customEmailParagraph: CustomEmailParagraph) => void
  setIsDialogOpen: (isDialogOpen: boolean) => void
}

const processPlainContent = (plainContent: string): string => {
  return (
    plainContent
      // Remove {{#if condition}} and {{/if}} - keeps the content inside
      .replace(/\{\{#if\s+[^}]+\}\}/gi, '')
      .replace(/\{\{\/if\}\}/gi, '')
      // Handle triple braces {{{variable}}} - these are for unescaped HTML content
      .replace(/\{\{\{([^}]+)\}\}\}/g, '[$1]')
      // Replace {{variable}} with [variable]
      .replace(/\{\{([^}]+)\}\}/g, '[$1]')
      .replace(/\s*\(\s*https?:\/\/[^\s)]+\s*\)/gi, ' [LINK]')
      .replace(/\n\s*\n\s*\n/g, '\n\n')
      .trim()
  )
}

const CustomParagraphModal = ({
  customEmailParagraph,
  title,
  templateName,
  initialState,
  saveCustomParagraph,
  setIsDialogOpen,
}: CustomParagraphModalProps) => {
  const [paragraphText, setParagraphText] = useState(
    customEmailParagraph?.customParagraph ?? ''
  )
  const queryClient = useQueryClient()
  const { getAuthToken } = useContext(AuthContext)

  const {
    data: templateData,
    isFetched,
    isLoading,
  } = useQuery<any | undefined>({
    queryKey: [`email-preview-${templateName}`],
    queryFn: () => GetSendGridTemplate(getAuthToken, templateName),
    enabled: !!templateName,
  })

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [`email-preview-${templateName}`],
    })
  }, [])

  const { templateContent, error } = useMemo(() => {
    if (!isFetched || !templateData) return { templateContent: '', error: '' }
    try {
      const activeVersion =
        templateData.versions?.find((v: any) => v.active === 1) ||
        templateData.versions?.[0]
      if (!activeVersion?.plain_content) {
        return {
          templateContent: '',
          error: 'No plain text content found in template',
        }
      }
      return {
        templateContent: processPlainContent(activeVersion.plain_content),
        error: '',
      }
    } catch (err) {
      return {
        templateContent: '',
        error: err instanceof Error ? err.message : 'An unknown error occurred',
      }
    }
  }, [isFetched, templateData])

  const handleEditorChange = useCallback((html: string, text: string) => {
    if (text.trim().length === 0) {
      setParagraphText('')
    } else if (text.length <= 500) {
      setParagraphText(html)
    }
  }, [])

  const handleSaveCustomParagraph = useCallback(() => {
    const customParagraph: CustomEmailParagraph = {
      state: customEmailParagraph?.state ?? initialState,
      customParagraph: paragraphText,
    }

    saveCustomParagraph(customParagraph)
    setIsDialogOpen(false)
  }, [
    customEmailParagraph,
    initialState,
    paragraphText,
    saveCustomParagraph,
    setIsDialogOpen,
  ])

  const modalContent = (
    <>
      <AgxRow
        id="custom-paragraph-modal"
        gap="spacing-5xl"
        padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
      >
        {isLoading && (
          <div className="custom-paragraph-loading">Loading template...</div>
        )}
        {!isLoading && !error && !templateContent && (
          <div>No template content available</div>
        )}
        {!isLoading && !error && templateContent && (
          <>
            <LexicalEditor
              id="customParagraph"
              label="Custom Paragraph"
              defaultValue={paragraphText}
              onChange={handleEditorChange}
              maxLength={500}
              stretch
              placeholder="Enter custom paragraph"
            />
            <EmailPreview templateContent={templateContent} />
          </>
        )}
      </AgxRow>
    </>
  )

  return (
    <AgxModal
      title={title}
      titleIcon={<Icon_Email color="var(--fg-action)" width={30} height={30} />}
      type="split"
      variant="withDivider"
      maxWidth="min(90vw, 1134px)"
      setIsDialogOpen={setIsDialogOpen}
      content={modalContent}
      actionButtonContainerStyle="withoutDivider"
      actionButtons={
        <AgxRow justifyContent="center" gap="spacing-lg" fill>
          <AgxButton
            id="cancel"
            variant="hollow"
            size="large"
            text="Cancel"
            onClick={() => setIsDialogOpen(false)}
          />
          <AgxButton
            id="saveChanges"
            size="large"
            variant="primary"
            text="Save Changes"
            onClick={() => {
              handleSaveCustomParagraph()
            }}
          />
        </AgxRow>
      }
    />
  )
}

export default CustomParagraphModal
